import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { pendingApprovalCountKey } from "@/hooks/usePendingApprovalCount";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

// A single pending action awaiting the owner's approval. Mirrors the shape used
// by the global Actions queue (ActionCenter), scoped here to one contact.
interface ApprovalRequest {
  id: string;
  action_name: string;
  inputs: any;
  reasoning_trace: string | null;
  status: "pending" | "approved" | "rejected" | "expired";
  created_at: string;
}

// Human-friendly names for raw action identifiers. Kept in sync with the
// global Actions queue without exposing internal risk tiers in the profile UI.
const ACTION_META: Record<string, { label: string }> = {
  send_message: { label: "AI reply" },
  create_reminder: { label: "Reminder" },
  escalate_conversation: { label: "Escalate to human" },
  open_case: { label: "Open case" },
  pause_outreach: { label: "Pause outreach" },
  create_booking: { label: "New booking" },
  cancel_booking: { label: "Cancel booking" },
  reschedule_booking: { label: "Reschedule booking" },
  send_payment_link: { label: "Payment link" },
  external_action: { label: "Run action" },
  mcp_action: { label: "Run MCP tool" },
};

const formatActionName = (name: string) =>
  ACTION_META[name]?.label ?? name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, " ");

// A queued AI reply stores its proposed text at inputs.content.text (a WhatsApp
// template at inputs.content.fallbackText) — show it so the owner can read
// exactly what will be sent before approving.
const replyDraftText = (inputs: any): string | null => {
  const t = inputs?.content?.text ?? inputs?.content?.fallbackText;
  return typeof t === "string" ? t : null;
};

// The "content" of an action. What it will actually send or do, in plain
// language (distinct from the "context", which is *why* the AI proposed it).
const actionContent = (req: ApprovalRequest): string | null => {
  const i = req.inputs ?? {};
  const draft = replyDraftText(i);
  if (draft) return draft;
  if (req.action_name === "send_payment_link") {
    const amount =
      typeof i.amount === "number"
        ? `${String(i.currency ?? "").toUpperCase()} ${(i.amount / 100).toFixed(2)}`.trim()
        : null;
    return [amount, i.description].filter(Boolean).join(" · ") || null;
  }
  if (typeof i.description === "string") return i.description;
  return null;
};

// Whether the content is a message to be sent verbatim (so we render it quoted).
const isMessageContent = (req: ApprovalRequest) => replyDraftText(req.inputs) != null;

const normEmail = (v: string | null | undefined) => (v ? v.trim().toLowerCase() : null);
const normPhone = (v: string | null | undefined) => (v ? v.replace(/\D/g, "") : null);
const normName = (v: string | null | undefined) => (v ? v.trim().toLowerCase() : null);

// Does this queued action belong to the given contact? Matches on the customer
// identity carried in the action's free-form `inputs` (the same fields the
// global queue groups by) against the contact's email / phone / name.
const matchesContact = (
  inputs: any,
  ids: { email: string | null; phone: string | null; name: string | null },
): boolean => {
  const i = inputs ?? {};
  const email = normEmail(i.customer_email ?? i.contact?.email);
  const phone = normPhone(i.customer_phone ?? i.contact?.phone);
  const name = normName(i.customer_name ?? i.contact?.name);
  if (ids.email && email && ids.email === email) return true;
  if (ids.phone && phone && ids.phone === phone) return true;
  // Name alone is a weak signal. Only use it when there's no contact info on
  // the action at all, to avoid colliding different people with the same name.
  if (ids.name && name && !email && !phone && ids.name === name) return true;
  return false;
};

export interface ContactApprovalQueueProps {
  businessId: string;
  contactId?: string | null;
  channelIdentifiers?: string[];
  email?: string | null;
  phone?: string | null;
  name?: string | null;
}

// Business-wide pending queue. Shared by every surface below; React Query
// dedupes by key so calling it from multiple places hits the network once.
function useApprovalQueueQuery(businessId: string) {
  return useQuery({
    queryKey: ["contact_approval_queue", businessId],
    queryFn: async (): Promise<ApprovalRequest[]> => {
      const { data, error } = await supabase
        .from("action_approval_queue")
        .select("id, action_name, inputs, reasoning_trace, status, created_at")
        .eq("business_id", businessId)
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ApprovalRequest[];
    },
    enabled: !!businessId,
  });
}

// Some queued actions (external_action / mcp_action) carry no customer identity
// in their inputs. Only a conversation_id. Resolve those conversations to their
// channel identity (customer_phone / channel_user_id) so the per-contact queue
// can still attribute them to the right contact.
type ConversationIdentity = { customer_phone: string | null; channel_user_id: string | null };

const queuedConversationId = (inputs: any): string | null =>
  typeof inputs?.conversation_id === "string" ? inputs.conversation_id : null;

function useQueueConversationIdentities(businessId: string) {
  const query = useApprovalQueueQuery(businessId);
  const convIds = useMemo(
    () =>
      Array.from(
        new Set(
          (query.data ?? [])
            .map((r) => queuedConversationId(r.inputs))
            .filter((v): v is string => !!v),
        ),
      ).sort(),
    [query.data],
  );
  const convQuery = useQuery({
    queryKey: ["contact_approval_queue_convs", businessId, convIds],
    queryFn: async (): Promise<Record<string, ConversationIdentity>> => {
      const { data, error } = await supabase
        .from("ai_conversations")
        .select("id, customer_phone, channel_user_id")
        .in("id", convIds);
      if (error) throw error;
      return Object.fromEntries(
        (data ?? []).map((c) => [c.id, { customer_phone: c.customer_phone, channel_user_id: c.channel_user_id }]),
      );
    },
    enabled: !!businessId && convIds.length > 0,
  });
  return convQuery.data ?? null;
}

// Fallback match: the action's conversation belongs to this contact (by the
// channel identity stored on the conversation. A phone number on WhatsApp, a
// channel-scoped user id on Telegram/Instagram, either of which we also store
// as the contact's phone).
const matchesViaConversation = (
  inputs: any,
  convs: Record<string, ConversationIdentity> | null,
  ids: { phone: string | null; channelIdentifiers?: Set<string> },
): boolean => {
  if (!convs) return false;
  const convId = queuedConversationId(inputs);
  if (!convId) return false;
  const conv = convs[convId];
  if (!conv) return false;
  if (ids.phone && (normPhone(conv.customer_phone) === ids.phone || normPhone(conv.channel_user_id) === ids.phone)) {
    return true;
  }
  const rawConversationIds = [conv.customer_phone, conv.channel_user_id]
    .filter((value): value is string => !!value)
    .map((value) => value.trim().toLowerCase());
  return rawConversationIds.some((value) => ids.channelIdentifiers?.has(value));
};

// Returns a `(contact) => pendingCount` lookup for the Contacts table, so each
// row can show a notification badge.
export function useContactApprovalCounts(businessId: string) {
  const query = useApprovalQueueQuery(businessId);
  const convs = useQueueConversationIdentities(businessId);
  const realItems = useMemo(
    () => (query.data ?? []).filter((r) => r.action_name !== "winback_batch"),
    [query.data],
  );
  return useMemo(
    () =>
      (c: { email?: string | null; phone?: string | null; name?: string | null; channel_identifiers?: string[] }): number => {
        const ids = {
          email: normEmail(c.email),
          phone: normPhone(c.phone),
          name: normName(c.name),
          channelIdentifiers: new Set((c.channel_identifiers ?? []).map((value) => value.trim().toLowerCase())),
        };
        return realItems.filter(
          (r) => matchesContact(r.inputs, ids) || matchesViaConversation(r.inputs, convs, ids),
        ).length;
      },
    [realItems, convs],
  );
}

// Shared source of truth for the per-contact queue. Used both by the tab badge
// (in CustomerDetails) and the tab content below.
export function useContactApprovalQueue({ businessId, contactId, channelIdentifiers = [], email, phone, name }: ContactApprovalQueueProps) {
  const query = useApprovalQueueQuery(businessId);
  const convs = useQueueConversationIdentities(businessId);

  const { data: storedIdentifiers = [] } = useQuery({
    queryKey: ["contact-approval-identifiers", contactId],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("channel_identifiers")
        .select("identifier_value")
        .eq("contact_id", contactId!);
      if (error) throw error;
      return (data ?? []).map((row) => row.identifier_value);
    },
    enabled: !!contactId,
  });

  const ids = useMemo(
    () => ({
      email: normEmail(email),
      phone: normPhone(phone),
      name: normName(name),
      channelIdentifiers: new Set(
        [...channelIdentifiers, ...storedIdentifiers].map((value) => value.trim().toLowerCase()),
      ),
    }),
    [channelIdentifiers, email, phone, name, storedIdentifiers],
  );

  // Per-contact actions only. Cross-contact campaign batches (winback_batch)
  // are reviewed as a whole in the global Actions queue, not per contact.
  const realItems = useMemo(
    () =>
      (query.data ?? []).filter(
        (req) =>
          req.action_name !== "winback_batch" &&
          (matchesContact(req.inputs, ids) || matchesViaConversation(req.inputs, convs, ids)),
      ),
    [query.data, ids, convs],
  );

  return { items: realItems, isLoading: query.isLoading };
}

const ContactApprovalQueue = (props: ContactApprovalQueueProps) => {
  const { businessId } = props;
  const queryClient = useQueryClient();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const { items, isLoading } = useContactApprovalQueue(props);

  const handleDecision = async (id: string, decision: "approve" | "reject") => {
    setProcessingId(id);
    try {
      const { data, error } = await (supabase.functions as any).invoke("execute-approved-action", {
        body: { queue_id: id, decision },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(decision === "approve" ? "Approved and sent" : "Rejected");
      queryClient.invalidateQueries({ queryKey: ["contact_approval_queue", businessId] });
      queryClient.invalidateQueries({ queryKey: ["action_approval_queue", businessId] });
      queryClient.invalidateQueries({ queryKey: pendingApprovalCountKey(businessId) });
    } catch (e: any) {
      toast.error(e.message || "Failed to process decision");
    } finally {
      setProcessingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card py-12 flex flex-col items-center justify-center gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="text-xs text-muted-foreground">Loading actions queue...</span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card py-12 flex flex-col items-center justify-center text-center">
        <div className="rounded-full bg-muted border border-border p-3 mb-2">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground font-medium">No actions awaiting approval for this contact.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="divide-y divide-border">
        {items.map((req) => (
          <div
            key={req.id}
            className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground" title={req.action_name}>
                  {formatActionName(req.action_name)}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDistanceToNow(new Date(req.created_at), { addSuffix: true })}
                </span>
              </div>
              {/* Content = what will actually be sent / done. */}
              {actionContent(req) && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    Content
                  </p>
                  <p className="text-xs text-foreground line-clamp-2">
                    {isMessageContent(req) ? `“${actionContent(req)}”` : actionContent(req)}
                  </p>
                </div>
              )}
              {/* Context = why the AI proposed this action. */}
              {req.reasoning_trace && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    Context
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{req.reasoning_trace}</p>
                </div>
              )}
            </div>
            <div className="flex gap-2.5 shrink-0 sm:pt-0.5">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 transition-colors"
                onClick={() => handleDecision(req.id, "reject")}
                disabled={processingId === req.id}
              >
                <XCircle className="h-3.5 w-3.5 mr-1" />
                Reject
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-sm transition-colors"
                onClick={() => handleDecision(req.id, "approve")}
                disabled={processingId === req.id}
              >
                {processingId === req.id ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                )}
                Approve
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContactApprovalQueue;
